import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Trash2, Plus, X, Check } from 'lucide-react';
import { Api } from '../../lib/api';
import { useUi } from '../../store/ui';

export function JobsTab() {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const q = useQuery({ queryKey: ['jobs'], queryFn: () => Api.jobs.list(), staleTime: 5_000, refetchInterval: 15_000 });
  const watchers = useQuery({ queryKey: ['watchers'], queryFn: Api.watchers.list, staleTime: 5_000, refetchInterval: 30_000 });
  const briefs = useQuery({ queryKey: ['briefs'], queryFn: Api.jobs.briefs, staleTime: 5_000 });
  const [newBrief, setNewBrief] = useState(false);
  const refresh = () => (qc.invalidateQueries({ queryKey: ['jobs'] }), qc.invalidateQueries({ queryKey: ['insights'] }));
  const runNow = async (kind: string) => {
    const j = await Api.jobs.run(kind);
    toast(`${kind}: ${j.status}${j.result ? ` · ${JSON.stringify(j.result).slice(0, 80)}` : ''}${j.lastError ? ` · ${j.lastError}` : ''}`, j.status === 'OK' ? 'info' : 'warn');
    refresh();
  };
  return (
    <div>
      <div className="admin-head"><span className="muted" style={{ fontSize: 12 }}>Workers run on a schedule (Brisbane time) and drop findings into the Insight inbox. Nothing is auto-applied. The digest is off by default.</span></div>
      <table className="admin-table">
        <thead><tr><th>Worker</th><th>Schedule</th><th>On</th><th>Last run</th><th>Result</th><th></th></tr></thead>
        <tbody>
          {(q.data?.schedule ?? []).map((s) => (
            <tr key={s.kind} className={s.enabled ? '' : 'dim'}>
              <td><b className="mono">{s.kind}</b><div className="faint" style={{ fontSize: 11 }}>{s.description}</div></td>
              <td className="mono">{s.cron}</td>
              <td><input type="checkbox" checked={s.enabled} onChange={(e) => void Api.jobs.setEnabled(s.kind, e.target.checked).then(refresh)} /></td>
              <td className="mono" style={{ fontSize: 11 }}>{s.lastRun ? `${new Date(s.lastRun.runAt).toLocaleString()} · ${s.lastRun.status}` : 'never'}</td>
              <td className="mono" style={{ fontSize: 11 }}>{s.lastRun?.result ? `${s.lastRun.result.findings ?? 0} findings, ${s.lastRun.result.created ?? 0} new, ${s.lastRun.result.superseded ?? 0} superseded` : s.lastRun?.lastError ?? ''}</td>
              <td><button className="ghost" onClick={() => void runNow(s.kind)}><Play size={12} /> Run now</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="admin-head" style={{ marginTop: 22 }}><span className="label">Watchers · {watchers.data?.watchers.length ?? 0} / {watchers.data?.max ?? 50}</span><span className="muted" style={{ fontSize: 12 }}>Saved diagnostics that raise an insight when a reading crosses a rule. Create one from any tool card ("Watch this"). They never page anyone.</span></div>
      <table className="admin-table">
        <thead><tr><th>Name</th><th>Tool</th><th>Rule</th><th>Cron</th><th>On</th><th>Last</th><th></th></tr></thead>
        <tbody>
          {(watchers.data?.watchers ?? []).map((w) => (
            <tr key={w.id} className={w.enabled ? '' : 'dim'}>
              <td><b>{w.name}</b>{w.firing && <span className="chip crit" style={{ marginLeft: 6, fontSize: 9 }}>firing</span>}</td>
              <td className="mono" style={{ fontSize: 11 }}>{w.tool} {String(w.input.target ?? w.input.id ?? w.input.url ?? '')}</td>
              <td className="mono" style={{ fontSize: 11 }}>{w.rule.field} {w.rule.type === 'delta' ? `Δ≥${w.rule.deltaAbs} vs median(${w.rule.window ?? 24})` : `${w.rule.op ?? 'gt'} ${JSON.stringify(w.rule.value)}`}</td>
              <td className="mono">{w.cron}</td>
              <td><input type="checkbox" checked={w.enabled} onChange={(e) => void Api.watchers.patch(w.id, { enabled: e.target.checked }).then(() => qc.invalidateQueries({ queryKey: ['watchers'] }))} /></td>
              <td className="mono" style={{ fontSize: 11 }}>{w.lastRunAt ? `${new Date(w.lastRunAt).toLocaleString()} → ${JSON.stringify(w.lastValue)}` : 'never'}{w.lastError && <div style={{ color: 'var(--down)' }}>{w.lastError}</div>}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button className="icon" title="Run now" onClick={() => void Api.watchers.run(w.id).then((j) => (toast(`${w.name}: ${JSON.stringify(j.result?.detail ?? j.status).slice(0, 100)}`), qc.invalidateQueries({ queryKey: ['watchers'] }), qc.invalidateQueries({ queryKey: ['insights'] })))}><Play size={13} /></button>
                <button className="icon danger" title="Delete" onClick={() => window.confirm(`Delete watcher "${w.name}"?`) && void Api.watchers.remove(w.id).then(() => qc.invalidateQueries({ queryKey: ['watchers'] }))}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
          {watchers.data?.watchers.length === 0 && <tr><td colSpan={7} className="faint">No watchers yet.</td></tr>}
        </tbody>
      </table>

      <div className="admin-head" style={{ marginTop: 22 }}><span className="label">Research briefs</span><span className="muted" style={{ fontSize: 12 }}>Scheduled research on a topic; each run proposes a reference note.</span><span className="spacer" /><button className="ghost" onClick={() => setNewBrief(true)}><Plus size={12} /> Add brief</button></div>
      <table className="admin-table">
        <thead><tr><th>Topic</th><th>Cron</th><th>On</th><th>Last run</th><th></th></tr></thead>
        <tbody>
          {(briefs.data?.briefs ?? []).map((b) => (
            <tr key={b.id} className={b.enabled ? '' : 'dim'}>
              <td>{b.topic}</td>
              <td className="mono">{b.cron}</td>
              <td>{b.enabled ? 'yes' : 'no'}</td>
              <td className="mono" style={{ fontSize: 11 }}>{b.lastRunAt ? new Date(b.lastRunAt).toLocaleString() : 'never'}{b.lastError && <div style={{ color: 'var(--down)' }}>{b.lastError}</div>}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button className="icon" title="Run now" onClick={() => void Api.jobs.runBrief(b.id).then((j) => (toast(`brief: ${j.status}${j.lastError ? ' · ' + j.lastError : ''}`), qc.invalidateQueries({ queryKey: ['briefs'] }), qc.invalidateQueries({ queryKey: ['insights'] })))}><Play size={13} /></button>
                <button className="icon danger" title="Delete" onClick={() => void Api.jobs.removeBrief(b.id).then(() => qc.invalidateQueries({ queryKey: ['briefs'] }))}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
          {briefs.data?.briefs.length === 0 && <tr><td colSpan={5} className="faint">No briefs.</td></tr>}
        </tbody>
      </table>

      <div className="admin-head" style={{ marginTop: 22 }}><span className="label">Recent job runs</span></div>
      <table className="admin-table">
        <thead><tr><th>When</th><th>Kind</th><th>Status</th><th>Result / error</th></tr></thead>
        <tbody>
          {(q.data?.jobs ?? []).slice(0, 40).map((j) => (
            <tr key={j.id}>
              <td className="mono" style={{ fontSize: 11 }}>{new Date(j.runAt).toLocaleString()}</td>
              <td className="mono">{j.kind}{j.payload?.watcherId ? ` (${String(j.payload.watcherId)})` : ''}</td>
              <td><span className={`chip ${j.status === 'OK' ? 'ok' : j.status === 'FAILED' ? 'crit' : j.status === 'RUNNING' ? 'teal' : ''}`} style={{ fontSize: 9 }}>{j.status}</span></td>
              <td className="mono" style={{ fontSize: 11, wordBreak: 'break-word' }}>{j.lastError ?? (j.result ? JSON.stringify(j.result).slice(0, 160) : '')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {newBrief && <BriefDialog onClose={() => (setNewBrief(false), qc.invalidateQueries({ queryKey: ['briefs'] }))} />}
    </div>
  );
}

function BriefDialog({ onClose }: { onClose: () => void }) {
  const toast = useUi((s) => s.toast);
  const [topic, setTopic] = useState('');
  const [cron, setCron] = useState('0 6 * * 1');
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" style={{ width: 460 }}>
        <div className="dialog-head">New research brief<span className="spacer" /><button className="icon" onClick={onClose}><X size={14} /></button></div>
        <div className="dialog-body">
          <label>Topic<input type="text" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Cambium PTP 670 firmware advisories" /></label>
          <label>Cron (Brisbane time)<input type="text" value={cron} onChange={(e) => setCron(e.target.value)} /></label>
        </div>
        <div className="dialog-foot">
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={topic.trim().length < 3} onClick={() => void Api.jobs.createBrief({ topic: topic.trim(), cron, enabled: true }).then(() => (toast('Brief scheduled'), onClose())).catch((e) => toast((e as Error).message, 'crit'))}><Check size={12} /> Schedule</button>
        </div>
      </div>
    </div>
  );
}
